"use client";

import { useApolloClient, useLazyQuery } from "@apollo/client/react";
import { useEffect, useState } from "react";

import { PostActivity } from "@/components/app/post/post-activity/post-activity";
import { CommentsProvider } from "@/components/app/post/post-comments/comments-context";
import { PostComments } from "@/components/app/post/post-comments/post-comments";
import { PostHeader } from "@/components/app/post/post-header";
import { PostNotFound } from "@/components/app/post/post-not-found";
import { SinglePostContent } from "@/components/app/post/single-post/single-post-content";
import type { PostType } from "@/contexts/feed-context";
import { useFeedContext } from "@/contexts/feed-context";
import { PostContextProvider } from "@/contexts/post-context";
import { PostByPostHashDocument } from "@/graphql/codegen/graphql";

import { PostHashLoading } from "./post-hash-loading";

interface PostPageContentProps {
  postHash: string;
}

export function PostPageContent({ postHash }: PostPageContentProps) {
  const [loading, setLoading] = useState(true);
  const [postByPostHash, setPostByPostHash] = useState<PostType | null>(null);
  const { getPostByPosthash } = useFeedContext();
  const apolloClient = useApolloClient();

  const [lazyLoadPost] = useLazyQuery(PostByPostHashDocument);

  useEffect(() => {
    const fetchPost = async () => {
      const post = getPostByPosthash(postHash);
      if (post) {
        setPostByPostHash(post);
        setLoading(false);
        return;
      }

      const request = lazyLoadPost({
        variables: {
          postHash,
        },
      });
      request.retain?.();
      const { data } = await request;
      setPostByPostHash(data?.postByPostHash ?? null);
      setLoading(false);
    };

    fetchPost();
  }, [getPostByPosthash, lazyLoadPost, postHash]);

  const poster = postByPostHash?.poster;
  const publicKey = poster?.publicKey ?? "";

  if (loading) {
    return <PostHashLoading />;
  }

  if (!postByPostHash || !publicKey) {
    return <PostNotFound />;
  }

  return (
    <PostContextProvider
      post={postByPostHash}
      disableReadMore
      updatePostByHash={(hash, post) => {
        const newPost =
          typeof post === "function" ? post(postByPostHash) : post;
        setPostByPostHash(newPost);

        try {
          apolloClient.cache.modify({
            id: apolloClient.cache.identify(newPost),
            // @ts-expect-error - This is a workaround for a bug in Apollo Client
            fields: newPost,
          });
        } catch {
          /* we don't care if it throws error */
        }
      }}
    >
      <PostHeader />
      <SinglePostContent />
      <PostActivity />
      <CommentsProvider
        {...{
          postCreatorPublicKey: postByPostHash?.poster?.publicKey ?? "",
        }}
      >
        <PostComments />
      </CommentsProvider>
    </PostContextProvider>
  );
}
